import React, { useEffect, useMemo, useState } from "react";
import {
  useGetEnglishQuizBlueprintByQuizQuery,
  useUpdateEnglishQuizBlueprintMutation,
  useDeleteEnglishQuizBlueprintMutation,
  useValidateEnglishQuizBlueprintMutation,
} from "../../slices/englishQuizBlueprintApiSlice";

import BlueprintSectionList from "./components/BlueprintSectionList";
import BlueprintTotals from "./components/BlueprintTotals";
import BlueprintValidationSummary from "./components/BlueprintValidationSummary";
import BlueprintValidationResult from "./components/BlueprintValidationResult";
import BlueprintActions from "./components/BlueprintActions";

const emptyBlueprint = {
  name: "",
  description: "",
  sections: [],
};

const emptySection = {
  name: "",
  description: "",
  order: 1,
  selectionType: "questions",
  questionType: "",
  groupType: "",
  difficulty: "",
  chapterId: "",
  selectionCount: 1,
  marksPerQuestion: 1,
  negativeMarks: 0,
  randomizeQuestions: false,
  randomizeOptions: false,
  randomizeSections: false,
};

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
}

function getBlueprint(response) {
  if (!response) {
    return null;
  }

  return (
    response?.data?.blueprint ||
    response?.data ||
    response?.blueprint ||
    response
  );
}

function getSections(blueprint) {
  if (Array.isArray(blueprint?.sections)) {
    return blueprint.sections;
  }

  if (Array.isArray(blueprint?.sectionSettings)) {
    return blueprint.sectionSettings;
  }

  return [];
}

function normalizeSection(section, index) {
  return {
    ...emptySection,
    ...section,
    id: section?.id || section?._id,
    order: Number(section?.order ?? index + 1),
    selectionCount: Number(
      section?.selectionCount ?? section?.count ?? 0
    ),
    marksPerQuestion: Number(
      section?.marksPerQuestion ??
        section?.marks ??
        0
    ),
    negativeMarks: Number(
      section?.negativeMarks ?? 0
    ),
  };
}

function normalizeBlueprint(blueprint) {
  return {
    ...emptyBlueprint,
    ...blueprint,
    name:
      blueprint?.name ||
      blueprint?.title ||
      "",
    description:
      blueprint?.description || "",
    sections: getSections(blueprint).map(
      normalizeSection
    ),
  };
}

function buildPayload(form, quizId) {
  return {
    quizId,
    name: form.name.trim(),
    description:
      form.description?.trim() || "",
    sections: form.sections.map(
      (section, index) => ({
        ...section,
        order: index + 1,
        selectionCount: Number(
          section.selectionCount || 0
        ),
        marksPerQuestion: Number(
          section.marksPerQuestion || 0
        ),
        negativeMarks: Number(
          section.negativeMarks || 0
        ),
      })
    ),
  };
}

export default function EnglishQuizBlueprintEdit({
  quizId,
  blueprintId,
  initialBlueprint = null,
  onSuccess,
  onCancel,
  className = "",
}) {
  const shouldFetch =
    Boolean(quizId) &&
    !initialBlueprint;

  const {
    data: fetchedBlueprint,
    isLoading: loadingBlueprint,
    isFetching: fetchingBlueprint,
    error: fetchError,
    refetch,
  } = useGetEnglishQuizBlueprintByQuizQuery(
    quizId,
    {
      skip: !shouldFetch,
    }
  );

  const [
    updateBlueprint,
    {
      isLoading: updating,
      error: updateError,
    },
  ] =
    useUpdateEnglishQuizBlueprintMutation();

  const [
    deleteBlueprint,
    {
      isLoading: deleting,
      error: deleteError,
    },
  ] =
    useDeleteEnglishQuizBlueprintMutation();

  const [
    validateBlueprint,
    {
      isLoading: validating,
      error: validationError,
    },
  ] =
    useValidateEnglishQuizBlueprintMutation();

  const [form, setForm] =
    useState(emptyBlueprint);

  const [originalForm, setOriginalForm] =
    useState(emptyBlueprint);

  const [
    validationResult,
    setValidationResult,
  ] = useState(null);

  const [formError, setFormError] =
    useState("");

  const [successMessage, setSuccessMessage] =
    useState("");

  const [confirmDelete, setConfirmDelete] =
    useState(false);

  const resolvedBlueprint =
    initialBlueprint ||
    getBlueprint(fetchedBlueprint);

  const resolvedBlueprintId =
    blueprintId ||
    resolvedBlueprint?.id ||
    resolvedBlueprint?._id ||
    resolvedBlueprint?.blueprintId;

  useEffect(() => {
    if (!resolvedBlueprint) {
      return;
    }

    const normalized =
      normalizeBlueprint(
        resolvedBlueprint
      );

    setForm(normalized);
    setOriginalForm(normalized);
    setValidationResult(null);
    setFormError("");
    setSuccessMessage("");
  }, [resolvedBlueprint]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(form) !==
      JSON.stringify(originalForm),
    [form, originalForm]
  );

  const totalQuestions = useMemo(
    () =>
      form.sections.reduce(
        (total, section) =>
          total +
          Number(
            section.selectionCount || 0
          ),
        0
      ),
    [form.sections]
  );

  const totalMarks = useMemo(
    () =>
      form.sections.reduce(
        (total, section) => {
          const count = Number(
            section.selectionCount || 0
          );

          const marks = Number(
            section.marksPerQuestion || 0
          );

          return total + count * marks;
        },
        0
      ),
    [form.sections]
  );

  const isFormValid = useMemo(() => {
    if (!quizId) {
      return false;
    }

    if (!resolvedBlueprintId) {
      return false;
    }

    if (!form.name.trim()) {
      return false;
    }

    if (!form.sections.length) {
      return false;
    }

    return form.sections.every(
      (section) =>
        Boolean(section.name?.trim()) &&
        Number(section.selectionCount) > 0
    );
  }, [
    quizId,
    resolvedBlueprintId,
    form.name,
    form.sections,
  ]);

  const isBusy =
    loadingBlueprint ||
    fetchingBlueprint ||
    updating ||
    deleting ||
    validating;

  const handleFieldChange = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormError("");
    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleSectionChange = (
    index,
    updates
  ) => {
    setForm((current) => ({
      ...current,
      sections: current.sections.map(
        (section, sectionIndex) =>
          sectionIndex === index
            ? {
                ...section,
                ...updates,
              }
            : section
      ),
    }));

    setFormError("");
    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleAddSection = () => {
    setForm((current) => ({
      ...current,
      sections: [
        ...current.sections,
        {
          ...emptySection,
          order:
            current.sections.length + 1,
        },
      ],
    }));

    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleRemoveSection = (
    index
  ) => {
    setForm((current) => ({
      ...current,
      sections: current.sections
        .filter(
          (_, sectionIndex) =>
            sectionIndex !== index
        )
        .map((section, sectionIndex) => ({
          ...section,
          order: sectionIndex + 1,
        })),
    }));

    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleMoveSection = (
    index,
    direction
  ) => {
    setForm((current) => {
      const sections = [
        ...current.sections,
      ];

      const targetIndex =
        direction === "up"
          ? index - 1
          : index + 1;

      if (
        targetIndex < 0 ||
        targetIndex >= sections.length
      ) {
        return current;
      }

      [
        sections[index],
        sections[targetIndex],
      ] = [
        sections[targetIndex],
        sections[index],
      ];

      return {
        ...current,
        sections: sections.map(
          (section, sectionIndex) => ({
            ...section,
            order: sectionIndex + 1,
          })
        ),
      };
    });

    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleSave = async () => {
    setFormError("");
    setSuccessMessage("");
    setValidationResult(null);

    if (!quizId) {
      setFormError(
        "Quiz ID is required to update the blueprint."
      );
      return;
    }

    if (!resolvedBlueprintId) {
      setFormError(
        "Blueprint ID is required to update the blueprint."
      );
      return;
    }

    if (!form.name.trim()) {
      setFormError(
        "Blueprint name is required."
      );
      return;
    }

    if (!form.sections.length) {
      setFormError(
        "Add at least one blueprint section."
      );
      return;
    }

    const invalidSection =
      form.sections.find(
        (section) =>
          !section.name?.trim() ||
          Number(section.selectionCount) <= 0
      );

    if (invalidSection) {
      setFormError(
        "Complete the section name and selection count before saving."
      );
      return;
    }

    try {
      const payload = buildPayload(
        form,
        quizId
      );

      const response =
        await updateBlueprint({
          id: resolvedBlueprintId,
          blueprintId:
            resolvedBlueprintId,
          ...payload,
        }).unwrap();

      const updatedBlueprint =
        getBlueprint(response);

      const normalized =
        normalizeBlueprint(
          updatedBlueprint || {
            ...form,
          }
        );

      setForm(normalized);
      setOriginalForm(normalized);
      setSuccessMessage(
        "Blueprint updated successfully."
      );

      onSuccess?.({
        blueprint:
          updatedBlueprint ||
          normalized,
        quizId,
      });
    } catch (error) {
      setFormError(
        getErrorMessage(error)
      );
    }
  };

  const handleValidate = async () => {
    setFormError("");
    setSuccessMessage("");

    if (!quizId) {
      setFormError(
        "Quiz ID is required before validation."
      );
      return;
    }

    if (!resolvedBlueprintId) {
      setFormError(
        "Blueprint ID is required before validation."
      );
      return;
    }

    if (!isFormValid) {
      setFormError(
        "Complete the blueprint before validating it."
      );
      return;
    }

    try {
      const response =
        await validateBlueprint({
          id: resolvedBlueprintId,
          blueprintId:
            resolvedBlueprintId,
          ...buildPayload(form, quizId),
        }).unwrap();

      setValidationResult(
        response?.data || response
      );
    } catch (error) {
      setValidationResult(null);
      setFormError(
        getErrorMessage(error)
      );
    }
  };

  const handleReset = () => {
    setForm(originalForm);
    setFormError("");
    setSuccessMessage("");
    setValidationResult(null);
  };

  const handleRefresh = async () => {
    setFormError("");
    setSuccessMessage("");
    setValidationResult(null);

    if (!refetch) {
      return;
    }

    try {
      await refetch();
    } catch (error) {
      setFormError(
        getErrorMessage(error)
      );
    }
  };

  const handleDelete = async () => {
    if (!resolvedBlueprintId) {
      setFormError(
        "Blueprint ID is required to delete the blueprint."
      );
      return;
    }

    setFormError("");
    setSuccessMessage("");

    try {
      await deleteBlueprint(
        resolvedBlueprintId
      ).unwrap();

      onSuccess?.({
        deleted: true,
        blueprintId:
          resolvedBlueprintId,
        quizId,
      });
    } catch (error) {
      setConfirmDelete(false);
      setFormError(
        getErrorMessage(error)
      );
    }
  };

  if (!quizId) {
    return (
      <div
        className={`rounded-xl border border-red-200 bg-red-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-red-900">
              Quiz ID is required
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              Provide the English quiz ID to edit
              its blueprint.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (
    loadingBlueprint ||
    fetchingBlueprint
  ) {
    return (
      <div
        className={`space-y-4 ${className}`}
      >
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="h-5 w-56 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-3 w-80 animate-pulse rounded bg-gray-100" />

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="h-11 animate-pulse rounded-lg bg-gray-100" />
            <div className="h-11 animate-pulse rounded-lg bg-gray-100" />
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />

          <div className="mt-5 space-y-3">
            {[1, 2, 3].map(
              (item) => (
                <div
                  key={item}
                  className="h-28 animate-pulse rounded-xl bg-gray-100"
                />
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  if (fetchError && !initialBlueprint) {
    return (
      <div
        className={`rounded-xl border border-red-200 bg-red-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">
              Unable to load blueprint
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              {getErrorMessage(
                fetchError
              )}
            </p>

            <button
              type="button"
              onClick={handleRefresh}
              className="mt-3 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resolvedBlueprint) {
    return (
      <div
        className={`rounded-xl border border-amber-200 bg-amber-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-amber-900">
              Blueprint not found
            </h3>

            <p className="mt-1 text-xs leading-5 text-amber-700">
              No English quiz blueprint is available
              for this quiz.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const sectionsForList =
    form.sections.map(
      (section, index) => ({
        ...section,
        id:
          section.id ||
          section._id ||
          `section-${index}`,
      })
    );

  return (
    <div
      className={`w-full space-y-6 ${className}`}
    >
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900">
                  Edit English Quiz Blueprint
                </h1>

                {resolvedBlueprint?.status && (
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                    {String(
                      resolvedBlueprint.status
                    ).replace(
                      /_/g,
                      " "
                    )}
                  </span>
                )}
              </div>

              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                Update the question selection rules,
                section structure, marks, difficulty,
                and randomization settings.
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 px-3 py-2 text-right">
              <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                Blueprint
              </p>

              <p className="mt-0.5 font-mono text-xs text-gray-700">
                {resolvedBlueprintId}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-5 sm:p-6">
          <div>
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-gray-900">
                Blueprint Details
              </h2>

              <p className="mt-1 text-xs text-gray-500">
                Update the basic information for this
                blueprint.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-gray-700">
                  Blueprint Name
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </span>

                <input
                  value={form.name}
                  onChange={(event) =>
                    handleFieldChange(
                      "name",
                      event.target.value
                    )
                  }
                  disabled={isBusy}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50"
                  placeholder="English Exam Blueprint"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-gray-700">
                  Description
                </span>

                <input
                  value={form.description}
                  onChange={(event) =>
                    handleFieldChange(
                      "description",
                      event.target.value
                    )
                  }
                  disabled={isBusy}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50"
                  placeholder="Optional blueprint description"
                />
              </label>
            </div>
          </div>

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-600"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>

                <p className="text-xs leading-5 text-red-700">
                  {formError}
                </p>
              </div>
            </div>
          )}

          {updateError &&
            !formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
                {getErrorMessage(
                  updateError
                )}
              </div>
            )}

          {deleteError &&
            !formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
                {getErrorMessage(
                  deleteError
                )}
              </div>
            )}

          <div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Blueprint Sections
                </h2>

                <p className="mt-1 text-xs text-gray-500">
                  Configure how questions are selected
                  for each section.
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddSection}
                disabled={isBusy}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Add Section
              </button>
            </div>

            <BlueprintSectionList
              sections={sectionsForList}
              onAddSection={
                handleAddSection
              }
              onRemoveSection={
                handleRemoveSection
              }
              onSectionChange={
                handleSectionChange
              }
              onMoveSection={
                handleMoveSection
              }
              disabled={isBusy}
            />
          </div>

          {form.sections.length > 0 && (
            <BlueprintTotals
              sections={form.sections}
              totalQuestions={
                totalQuestions
              }
              totalMarks={totalMarks}
            />
          )}

          {validationResult && (
            <BlueprintValidationSummary
              result={validationResult}
              onValidate={
                handleValidate
              }
            />
          )}

          {validationResult && (
            <BlueprintValidationResult
              result={validationResult}
              loading={validating}
              error={validationError}
              onRetry={handleValidate}
            />
          )}

          {successMessage && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                <p className="text-xs font-medium text-emerald-700">
                  {successMessage}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"
                />
              </svg>
            </div>

            <h3 className="mt-4 text-base font-semibold text-gray-900">
              Delete blueprint?
            </h3>

            <p className="mt-2 text-sm leading-6 text-gray-500">
              This action will permanently remove
              the English quiz blueprint. This
              cannot be undone.
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  setConfirmDelete(false)
                }
                disabled={deleting}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {deleting && (
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="3"
                      className="opacity-25"
                    />
                    <path
                      d="M21 12a9 9 0 00-9-9"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                Delete Blueprint
              </button>
            </div>
          </div>
        </div>
      )}

      <BlueprintActions
        onSave={handleSave}
        onValidate={handleValidate}
        onDelete={() =>
          setConfirmDelete(true)
        }
        onRefresh={handleRefresh}
        onReset={handleReset}
        onCancel={onCancel}
        isDirty={isDirty}
        isValid={isFormValid}
        saving={updating}
        validating={validating}
        deleting={deleting}
        refreshing={
          fetchingBlueprint ||
          loadingBlueprint
        }
        disabled={isBusy}
        showPublish={false}
        showArchive={false}
        showActivate={false}
        showDelete
        showRefresh
        showReset
        showCancel={Boolean(onCancel)}
        showSecondaryActions
        saveLabel="Save Changes"
        validateLabel="Validate Blueprint"
        deleteLabel="Delete Blueprint"
        refreshLabel="Refresh"
        resetLabel="Discard Changes"
        sticky
      />
    </div>
  );
}